import React, { useState, Fragment, useEffect } from 'react';
import { VTEquipmentAnomalyData, VTCoverData, PDFImageProps } from '../../../../utils/models';

import Button from '../../../themed/button';
import Conditional from '../../../common/conditional';
import Elevation from '../../../themed/instruction-modals/elevation';
import Modal from '../../../themed/modal';
import RiskRating from '../riskRating';
import VTEquipmentReport from '.';
import VTCoverForm from '../../../forms/vt/vtCoverForm';
import VTEquipmentSummary from './vtEquipmentSummary';
import VTPipingReportSummary from '../../../forms/vt/vtPipingReportSummary';
import VTPhotoRecordForm from '../../../forms/vt/vtPhotoRecordForm';
import Zippy from '../../../themed/zippy';

import { tutorialConstants } from '../../../../utils/constants';
import { getTutorials } from '../../../../store/tutorials';
import { useAppSelector } from '../../../../store/configureStore';

import useAutoSaveReport from '../../../../hooks/useAutoSave';
import useProjectDetails from '../../../../hooks/useProjectDetails';

import config from '../../../../utils/config';

const CreateVTEquipmentReport: React.FC = () => {
	const [previewReport, setPreviewReport] = useState<boolean>(false);
	const [vtCoverData, setVTCoverData] = useState<VTCoverData | null>(null);
	const [vtEquipmentSummary, setVTEquipmentSummary] = useState<string>('');
	const [vtPhoto, setVTPhoto] = useState<PDFImageProps[]>([]);
	const [vtEquipmentData, setVTEquipmentData] = useState<VTEquipmentAnomalyData[]>([]);

	const togglePreviewReport = (): void => {
		setPreviewReport((p: boolean) => !p);
	};

	const getReportDetails = () => {
		return {
			anomalyData: vtEquipmentData,
			vtEquipmentReportSummary: vtEquipmentSummary,
			vtSummaryData: vtEquipmentData,
			projectDetails,
			coverSheetData: vtCoverData,
			photoRecord: vtPhoto,
		};
	};

	const handleSaveAndPreview = (): void => {
		autoSave.saveReport();
		togglePreviewReport();
	};

	const { projectDetails, error } = useProjectDetails();
	const { activeTutorial } = useAppSelector(getTutorials);

	const reportDetails = getReportDetails();
	const autoSave = useAutoSaveReport(reportDetails);

	useEffect(() => {
		type ReportData = ReturnType<typeof getReportDetails>;
		const cachedReport = autoSave.oldReportData as ReportData | null;

		if (cachedReport) {
			setVTCoverData(cachedReport.coverSheetData);
			setVTEquipmentData(cachedReport.vtSummaryData);
			setVTEquipmentSummary(cachedReport.vtEquipmentReportSummary);
			setVTPhoto(cachedReport.photoRecord);
		}
	}, [autoSave.oldReportData]);

	return (
		<Fragment>
			<Modal isVisible={previewReport} onClose={togglePreviewReport}>
				<VTEquipmentReport {...reportDetails} />
			</Modal>

			<Zippy headerTitle="Cover Sheet" isValid={!!vtCoverData}>
				<VTCoverForm onProceed={setVTCoverData} />
			</Zippy>

			<Zippy
				headerTitle="Report Summary"
				isValid={vtEquipmentSummary.length > 0}
			>
				<VTPipingReportSummary onProceed={setVTEquipmentSummary} />
			</Zippy>

			<Zippy headerTitle="Anomaly Sheet" isValid={vtEquipmentData.length > 0}>
				<VTEquipmentSummary
					anomalyData={vtEquipmentData}
					onDataChange={setVTEquipmentData}
				/>
			</Zippy>

			<Zippy headerTitle="Photographic Record" isValid={vtPhoto.length > 0}>
				<VTPhotoRecordForm onProceed={setVTPhoto} />
			</Zippy>

			<Conditional isVisible={config.enableRiskRating}>
				<Zippy headerTitle="Risk Rating" isValid={vtEquipmentData.length > 0}>
					<RiskRating data={vtEquipmentData} />
				</Zippy>
			</Conditional>

			<div className="d-flex justify-content-end">
				<Elevation
					isElevated={activeTutorial === tutorialConstants.PREVIEW_REPORT}
				>
					<Button
						label="Save and Preview Report"
						disabled={error}
						onClick={handleSaveAndPreview}
					/>
				</Elevation>
			</div>
		</Fragment>
	);
};

export default CreateVTEquipmentReport;
